import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, Validators } from "@angular/forms";
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Observable,
  startWith,
  switchMap,
} from "rxjs";

import { PolicyService } from "@bitwarden/common/admin-console/abstractions/policy/policy.service.abstraction";
import { PolicyType } from "@bitwarden/common/admin-console/enums";
import { getFirstPolicy } from "@bitwarden/common/admin-console/services/policy/default-policy.service";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { DomainSettingsService } from "@bitwarden/common/autofill/services/domain-settings.service";
import { MaximumSessionTimeoutPolicyData } from "@bitwarden/common/key-management/session-timeout";
import {
  VaultTimeout,
  VaultTimeoutAction,
  VaultTimeoutOption,
  VaultTimeoutSettingsService,
  VaultTimeoutStringType,
} from "@bitwarden/common/key-management/vault-timeout";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { Theme, ThemeTypes } from "@bitwarden/common/platform/enums";
import { Utils } from "@bitwarden/common/platform/misc/utils";
import { ThemeStateService } from "@bitwarden/common/platform/theming/theme-state.service";
import { UserId } from "@bitwarden/common/types/guid";
import { DialogService, ToastService } from "@bitwarden/components";
import {
  SessionTimeoutInputComponent,
  SessionTimeoutSettingsComponentService,
} from "@bitwarden/key-management-ui";

import { HeaderModule } from "../layouts/header/header.module";
import { SharedModule } from "../shared";

type LocaleOption = {
  name: string;
  value: string | null;
};

type ThemeOption = {
  name: string;
  value: Theme;
};

@Component({
  selector: "app-appearance",
  templateUrl: "appearance.component.html",
  imports: [SharedModule, HeaderModule, SessionTimeoutInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppearanceComponent implements OnInit {
  readonly VaultTimeoutAction = VaultTimeoutAction;

  readonly localeOptions: LocaleOption[];
  readonly themeOptions: ThemeOption[];

  readonly availableTimeoutActions = signal<VaultTimeoutAction[]>([]);
  readonly isTimeoutSuppressed = signal(false);
  readonly sessionTimeoutActionFromPolicy = signal<VaultTimeoutAction | null>(null);

  readonly availableTimeoutOptions$: Observable<VaultTimeoutOption[]>;

  readonly form = this.formBuilder.group({
    timeout: this.formBuilder.control<VaultTimeout | null>(null, [Validators.required]),
    timeoutAction: this.formBuilder.control<VaultTimeoutAction>(VaultTimeoutAction.Lock, [
      Validators.required,
    ]),
    enableFavicons: this.formBuilder.control(true),
    theme: this.formBuilder.control<Theme>(ThemeTypes.Light, [Validators.required]),
    locale: this.formBuilder.control<string | null>(null),
  });

  private readonly userId = signal<UserId | null>(null);
  private readonly initialTimeout = signal<VaultTimeout | null>(null);
  private readonly initialTimeoutAction = signal<VaultTimeoutAction | null>(null);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly i18nService: I18nService,
    private readonly themeStateService: ThemeStateService,
    private readonly domainSettingsService: DomainSettingsService,
    private readonly accountService: AccountService,
    private readonly vaultTimeoutSettingsService: VaultTimeoutSettingsService,
    private readonly sessionTimeoutSettingsComponentService: SessionTimeoutSettingsComponentService,
    private readonly policyService: PolicyService,
    private readonly dialogService: DialogService,
    private readonly toastService: ToastService,
    private readonly destroyRef: DestroyRef,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    const localeOptions: LocaleOption[] = [];
    i18nService.supportedTranslationLocales.forEach((locale) => {
      let name = locale;
      if (i18nService.localeNames.has(locale)) {
        name += " - " + i18nService.localeNames.get(locale);
      }
      localeOptions.push({ name: name, value: locale });
    });
    localeOptions.sort(Utils.getSortFunction(i18nService, "name"));
    localeOptions.splice(0, 0, { name: i18nService.t("default"), value: null });
    this.localeOptions = localeOptions;
    this.themeOptions = [
      { name: i18nService.t("themeLight"), value: ThemeTypes.Light },
      { name: i18nService.t("themeDark"), value: ThemeTypes.Dark },
      { name: i18nService.t("themeSystem"), value: ThemeTypes.System },
    ];

    this.availableTimeoutOptions$ = this.accountService.activeAccount$.pipe(
      getUserId,
      switchMap((userId) =>
        this.sessionTimeoutSettingsComponentService.policyFilteredTimeoutOptions$(userId),
      ),
    );
  }

  async ngOnInit() {
    this.userId.set(await firstValueFrom(getUserId(this.accountService.activeAccount$)));

    const timeout = await firstValueFrom(
      this.vaultTimeoutSettingsService.getVaultTimeoutByUserId$(this.userId()!),
    );
    const timeoutAction = await firstValueFrom(
      this.vaultTimeoutSettingsService.getVaultTimeoutActionByUserId$(this.userId()!),
    );

    this.initialTimeout.set(timeout);
    this.initialTimeoutAction.set(timeoutAction);

    this.form.setValue(
      {
        timeout,
        timeoutAction,
        enableFavicons: await firstValueFrom(this.domainSettingsService.showFavicons$),
        theme: await firstValueFrom(this.themeStateService.selectedTheme$),
        locale: (await firstValueFrom(this.i18nService.userSetLocale$)) ?? null,
      },
      { emitEvent: false },
    );

    this.vaultTimeoutSettingsService
      .getVaultTimeoutByUserId$(this.userId()!)
      .pipe(
        filter((updatedTimeout) => this.form.controls.timeout.value !== updatedTimeout),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((updatedTimeout) => {
        this.form.controls.timeout.setValue(updatedTimeout, { emitEvent: false });
        this.initialTimeout.set(updatedTimeout);
      });

    new BehaviorSubject<void>(undefined)
      .pipe(
        startWith(undefined),
        switchMap(() =>
          combineLatest([
            this.vaultTimeoutSettingsService.availableVaultTimeoutActions$(this.userId()!),
            this.vaultTimeoutSettingsService.getVaultTimeoutActionByUserId$(this.userId()!),
            this.policyService.policiesByType$(PolicyType.MaximumVaultTimeout, this.userId()!).pipe(
              getFirstPolicy,
              map(
                (policy) =>
                  (policy?.data as MaximumSessionTimeoutPolicyData | undefined)?.action ?? null,
              ),
            ),
            this.vaultTimeoutSettingsService.isVaultTimeoutSuppressed$(this.userId()!),
          ]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([availableActions, action, sessionTimeoutActionFromPolicy, isSuppressed]) => {
        this.availableTimeoutActions.set(availableActions);
        this.sessionTimeoutActionFromPolicy.set(sessionTimeoutActionFromPolicy);
        this.isTimeoutSuppressed.set(isSuppressed);
        this.form.controls.timeoutAction.setValue(action, { emitEvent: false });
        this.initialTimeoutAction.set(action);

        if (isSuppressed) {
          this.form.controls.timeout.disable({ emitEvent: false });
          this.form.controls.timeoutAction.disable({ emitEvent: false });
        } else {
          this.form.controls.timeout.enable({ emitEvent: false });
          this.form.controls.timeoutAction.enable({ emitEvent: false });

          if (sessionTimeoutActionFromPolicy != null || availableActions.length <= 1) {
            this.form.controls.timeoutAction.disable({ emitEvent: false });
          }
        }
      });
  }

  async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { timeout, timeoutAction, enableFavicons, theme, locale } = this.form.getRawValue();

    if (timeout == null || timeoutAction == null || enableFavicons == null || theme == null) {
      return;
    }

    if (timeout !== this.initialTimeout()) {
      const timeoutSaved = await this.saveTimeout(this.initialTimeout()!, timeout);
      if (!timeoutSaved) {
        return;
      }
      this.initialTimeout.set(timeout);
    }

    if (timeoutAction !== this.initialTimeoutAction()) {
      const actionSaved = await this.saveTimeoutAction(timeoutAction);
      if (!actionSaved) {
        return;
      }
      this.initialTimeoutAction.set(timeoutAction);
    }

    await this.domainSettingsService.setShowFavicons(enableFavicons);
    await this.themeStateService.setSelectedTheme(theme);

    const currentLocale = (await firstValueFrom(this.i18nService.userSetLocale$)) ?? null;
    if (locale !== currentLocale) {
      await this.i18nService.setLocale(locale);
      window.location.reload();
      return;
    }

    this.toastService.showToast({
      variant: "success",
      message: this.i18nService.t("preferencesUpdated"),
    });
  }

  private async saveTimeout(previousValue: VaultTimeout, newValue: VaultTimeout): Promise<boolean> {
    this.form.controls.timeout.markAsTouched();
    if (this.form.controls.timeout.invalid) {
      return false;
    }

    if (newValue === VaultTimeoutStringType.Never) {
      const confirmed = await this.dialogService.openSimpleDialog({
        title: { key: "warning" },
        content: { key: "neverLockWarning" },
        type: "warning",
      });

      if (!confirmed) {
        this.form.controls.timeout.setValue(previousValue, { emitEvent: false });
        this.changeDetectorRef.markForCheck();
        return false;
      }
    }

    await this.vaultTimeoutSettingsService.setVaultTimeoutOptions(
      this.userId()!,
      newValue,
      this.form.controls.timeoutAction.value ?? VaultTimeoutAction.Lock,
    );

    this.sessionTimeoutSettingsComponentService.onTimeoutSave(newValue);
    return true;
  }

  private async saveTimeoutAction(value: VaultTimeoutAction): Promise<boolean> {
    if (value === VaultTimeoutAction.LogOut) {
      const confirmed = await this.dialogService.openSimpleDialog({
        title: { key: "vaultTimeoutLogOutConfirmationTitle" },
        content: { key: "vaultTimeoutLogOutConfirmation" },
        type: "warning",
      });

      if (!confirmed) {
        this.form.controls.timeoutAction.setValue(this.initialTimeoutAction()!, {
          emitEvent: false,
        });
        this.changeDetectorRef.markForCheck();
        return false;
      }
    }

    if (this.form.controls.timeout.hasError("policyError")) {
      this.toastService.showToast({
        variant: "error",
        message: this.i18nService.t("vaultTimeoutTooLarge"),
      });
      return false;
    }

    await this.vaultTimeoutSettingsService.setVaultTimeoutOptions(
      this.userId()!,
      this.form.controls.timeout.value!,
      value,
    );

    return true;
  }
}
